import { ChangeDetectorRef, Component, NgZone, OnDestroy, OnInit, inject } from '@angular/core';
import { Subscription } from 'rxjs';
import { ModalController } from '@ionic/angular';

import { FridgeCommand, PaymentDevice } from 'package-types';
import { VnStat } from 'package-types/utils';

import { FridgeService } from '../../services/fridge/fridge.service';
import { Api } from '../../services/api/api.service';
import { NotificationService } from '../../services/notification/notification.service';
import { SocketService } from '../../services/socket/socket.service';
import { UserService } from '../../services/user/user.service';

import { ConfirmationModalComponent } from '../confirmation-modal/confirmation-modal.component';
import { TranslateService } from '@ngx-translate/core';
import { ReservationConfig } from 'package-types/index';
import { DoorReservationConfig, FridgeEndOfDayRequestSource } from 'package-types/devices';
import { ProductService } from '../../services/product/product.service';
import { BoUpdateFridgeSettingsArguments } from 'package-types/devices/actions';

// must match END_OF_DAY_REQUEST_TTL_MS in devices-service
const END_OF_DAY_REQUEST_TTL_MS = 10 * 60 * 1000;
// end of day is scheduled daily (23:55 by default), so a successful one older than this was missed
const END_OF_DAY_STALE_MS = 26 * 60 * 60 * 1000;
// device accepts manual end of day only outside of a purchasing session (see fridge-device-service)
const END_OF_DAY_ALLOWED_STATES = [
  'WaitForCard',
  'WaitForAgeVerification',
  'WaitForSessionStart',
  'CustomerIdentification',
  'Error',
  'Service',
  'Factory',
  'StopSale',
];

interface TrafficItem {
  title: string;
  rx: string;
  tx: string;
  total: string;
}

@Component({
  selector: 'app-subsystem-modal',
  templateUrl: './subsystem-modal.component.html',
  styleUrls: ['./subsystem-modal.component.scss'],
  standalone: false,
})
export class SubsystemModalComponent implements OnInit, OnDestroy {
  private modalController = inject(ModalController);
  fridgeService = inject(FridgeService);
  productService = inject(ProductService);
  userService = inject(UserService);
  private notificationService = inject(NotificationService);
  private socketService = inject(SocketService);
  i18n = inject(TranslateService);
  private cdRef = inject(ChangeDetectorRef);
  private ngZone = inject(NgZone);

  private dataChangeSub: Subscription | null = null;

  objectKeys = Object.keys;
  // Local copies for reliable detectChanges() rendering
  stateMachineState = '';
  stateMachinePreviousState = '';
  stateMachineReason = '';
  subsystemsState: Record<string, string> = {};
  reservationAmount = '';
  isReservationAmountPerDoor = false;
  reservationAmountPerDoor: DoorReservationConfig[] = [];
  serviceCode = '';
  amountModalStatus = false;
  accessModalStatus = false;
  loadingPos = false;
  loadingEmployee = false;
  loadingEndOfDay = false;
  posTerminals: { ip: string; terminalId?: string }[];
  traffic: VnStat.Interface['traffic'];
  trafficItems: TrafficItem[];
  loadingTraffic = false;
  rackSize = 'big';
  showAiRecognition = false;
  doorsCount = 0;
  soundVolume = 100;
  initialSoundVolume = 100;
  savingVolume = false;
  volumeModalStatus = false;
  tempSoundVolume = 100;
  get volumeChanged(): boolean {
    return Math.abs(this.soundVolume - this.initialSoundVolume) > 0.0001;
  }

  paymentDevice = PaymentDevice;

  get isEndOfDayRelevant(): boolean {
    const device = this.fridgeService.activeFridge?.payment?.paymentDevice;
    return device === PaymentDevice.IP_ZVT || device === PaymentDevice.IP_TIM;
  }

  get isEndOfDayNeeded(): boolean {
    const fridge = this.fridgeService.activeFridge;
    if (!fridge) return false;
    if (fridge.endOfDayStatus?.needEndOfDay) return true;
    if (!fridge.lastEndOfDayAt) return true;
    return Date.now() - new Date(fridge.lastEndOfDayAt).getTime() >= END_OF_DAY_STALE_MS;
  }

  get isEndOfDayInProgress(): boolean {
    const requestedAt = this.fridgeService.activeFridge?.endOfDayRequest?.requestedAt;
    return (
      !!requestedAt && Date.now() - new Date(requestedAt).getTime() < END_OF_DAY_REQUEST_TTL_MS
    );
  }

  get canRunEndOfDay(): boolean {
    return (
      !!this.fridgeService.activeFridge?.connected &&
      this.isEndOfDayRelevant &&
      this.isEndOfDayNeeded &&
      END_OF_DAY_ALLOWED_STATES.includes(this.stateMachineState) &&
      !this.isEndOfDayInProgress &&
      !this.loadingEndOfDay
    );
  }

  ngOnInit() {
    this.rackSize = localStorage.getItem('fridgePageRackSize') ?? 'big';
    this.showAiRecognition = localStorage.getItem('showAiRecognition') === 'true';
    this.initReservationAmount();
    this.doorsCount = this.productService.productPlacement.length;
    const persistedVolume =
      (this.fridgeService.activeFridge?.tabletSetting as any)?.soundVolume ?? 100;
    this.initialSoundVolume = this.clampVolume(persistedVolume);
    this.soundVolume = this.initialSoundVolume;
    this.tempSoundVolume = this.soundVolume;
    this.syncFridgeData();

    this.dataChangeSub = this.fridgeService.fridgeDataChanged.subscribe(() => {
      this.tick();
    });
  }

  ngOnDestroy() {
    this.dataChangeSub?.unsubscribe();
  }

  private syncFridgeData() {
    const sm = this.fridgeService.activeFridge?.stateMachine;
    this.stateMachineState = sm?.state || '';
    this.stateMachinePreviousState = sm?.previousState || '';
    this.stateMachineReason = (sm as any)?.reason || '';
    this.subsystemsState = { ...(this.fridgeService.activeFridge?.subSystemsState || {}) };
  }

  private tick() {
    this.syncFridgeData();
    this.ngZone.run(() => this.cdRef.detectChanges());
  }

  async back() {
    await this.modalController.dismiss();
  }

  getFormattedName(text) {
    return text.replace(/([a-z])([A-Z])/g, '$1 $2');
  }

  async executeFridgeCommand(command) {
    let title: string;
    let text: string;
    switch (command) {
      case FridgeCommand.Shutdown:
        title = this.i18n.instant('systemModal.modals.sd_title');
        text = this.i18n.instant('systemModal.modals.sd_text');
        break;
      case FridgeCommand.Reboot:
        title = this.i18n.instant('systemModal.modals.rb_title');
        text = this.i18n.instant('systemModal.modals.rb_text');
        break;
      case FridgeCommand.ResetInventory:
        title = this.i18n.instant('systemModal.modals.ri_title');
        text = this.i18n.instant('systemModal.modals.ri_text');
        break;
      case FridgeCommand.RestartService:
        title = this.i18n.instant('systemModal.modals.rs_title');
        text = '';
        break;
      case FridgeCommand.AddEmployeeCard:
      case FridgeCommand.RemoveEmployeeCard:
        title = this.i18n.instant('systemModal.modals.e_title');
        text = '';
        break;
      case FridgeCommand.SearchPOS:
        title = this.i18n.instant('systemModal.modals.sp_title');
        text = this.i18n.instant('systemModal.modals.sp_text');
        break;
      case 'setZero':
        title = this.i18n.instant('systemModal.modals.sz_title');
        text = this.i18n.instant('systemModal.modals.sz_title');
        break;
      case 'startDemoPurchase':
        title = this.i18n.instant('systemModal.modals.sdp_title');
        text = this.i18n.instant('systemModal.modals.sdp_text');
        break;
      default:
        title = this.i18n.instant('systemModal.modals.d_title');
        text = '';
    }

    const { success } = await ConfirmationModalComponent.show(this.modalController, title, text);

    if (!success) {
      return;
    }

    if (command === 'setZero') {
      await Api.setZero(this.fridgeService.activeFridge._id);
    } else if (command === FridgeCommand.SearchPOS) {
      this.posTerminals = [];
      this.loadingPos = true;
      // Render the "searching" state right away — the component only re-renders on explicit tick().
      this.tick();
      try {
        const { posTerminals } = await Api.executeFridgeCommand(
          this.fridgeService.activeFridge._id,
          command,
        );

        if (posTerminals.length === 0) {
          await this.notificationService.notifyError(
            this.i18n.instant('notification.error.no_pos'),
          );
          return;
        }

        this.posTerminals = posTerminals;
      } catch (e) {
        this.loadingPos = false;
      } finally {
        this.loadingPos = false;
        this.tick();
      }
    } else if (
      command === FridgeCommand.AddEmployeeCard ||
      command === FridgeCommand.RemoveEmployeeCard
    ) {
      this.loadingEmployee = true;
      try {
        const { employeeCard } = await Api.executeFridgeCommand(
          this.fridgeService.activeFridge._id,
          command,
        );
        employeeCard
          ? await this.notificationService.notify(
              `Employee card #${employeeCard} was ${
                command === FridgeCommand.AddEmployeeCard ? 'added' : 'removed'
              }`,
            )
          : await this.notificationService.notifyError(
              this.i18n.instant('notification.error.no_e_card'),
            );
      } catch (e) {
        this.loadingEmployee = false;
      } finally {
        this.loadingEmployee = false;
        this.tick();
      }
      return;
    } else {
      await Api.executeFridgeCommand(this.fridgeService.activeFridge._id, command);
    }

    const msg = await this.resolveTranslation('notification.success.command', { command });
    await this.notificationService.notify(msg);
  }

  async runEndOfDay() {
    const fridge = this.fridgeService.activeFridge;
    if (!fridge) return;

    const { success } = await ConfirmationModalComponent.show(
      this.modalController,
      this.i18n.instant('systemModal.modals.eod_title'),
      this.i18n.instant('systemModal.modals.eod_text'),
    );
    if (!success) return;

    this.loadingEndOfDay = true;
    this.tick();
    try {
      const res = await Api.runFridgeEndOfDay(fridge._id);
      if (res?.success === true) {
        fridge.endOfDayRequest = {
          requestedAt: new Date(),
          source: FridgeEndOfDayRequestSource.Manual,
        };
        await this.notificationService.notify(this.i18n.instant('notification.success.end_of_day'));
      }
    } catch (e) {
      // error notification is shown by the axios interceptor
    } finally {
      this.loadingEndOfDay = false;
      this.tick();
    }
  }


  setModalInfo(amount: string) {
    this.reservationAmount = amount;
    this.amountModalStatus = true;
    this.tick();
  }

  setAccessModalInfo(serviceCode?: { code: string; enabled: boolean }) {
    this.serviceCode = serviceCode?.code || '';
    this.accessModalStatus = true;
    this.tick();
  }

  async saveReservationAmount() {
    const config: Partial<BoUpdateFridgeSettingsArguments['params']> = {
      reservationAmount: this.reservationAmount as any,
      doorReservationConfig: null,
    };
    if (this.isReservationAmountPerDoor) {
      config.doorReservationConfig = this.reservationAmountPerDoor;
    }
    const { fridge } = await Api.setFridgeSettings(config, this.fridgeService.activeFridge._id);

    this.fridgeService.setActiveFridge({ ...fridge, stateMachine: this.fridgeService.activeFridge?.stateMachine ?? fridge?.stateMachine });
    this.amountModalStatus = false;
  }

  initReservationAmount() {
    this.isReservationAmountPerDoor = this.getDoorsReservationAmount().length > 0;
    if (this.isReservationAmountPerDoor) {
      this.reservationAmountPerDoor = this.getDoorsReservationAmount();
    } else {
      this.reservationAmountPerDoor = this.productService.productPlacement.map((pp) => {
        return {
          rackNumber: pp.number,
          reservationAmount:
            (this.fridgeService.activeFridge.payment as ReservationConfig)?.reservationAmount || 1,
        } as DoorReservationConfig;
      });
    }
  }

  async enableServiceCode(enabled: boolean) {
    const { fridge } = await Api.setFridgeSettings(
      {
        serviceCode: {
          enabled,
          code: this.serviceCode,
        },
      },
      this.fridgeService.activeFridge._id,
    );

    this.fridgeService.setActiveFridge({ ...fridge, stateMachine: this.fridgeService.activeFridge?.stateMachine ?? fridge?.stateMachine });
    this.accessModalStatus = false;
  }

  async setTabletSettingSound(soundEnabled: boolean) {
    const { fridge } = await Api.setFridgeSettings(
      { soundEnabled },
      this.fridgeService.activeFridge._id,
    );

    this.fridgeService.setActiveFridge({ ...fridge, stateMachine: this.fridgeService.activeFridge?.stateMachine ?? fridge?.stateMachine });
    this.tick();
  }

  async saveTabletSoundVolume(volume?: number) {
    if (this.savingVolume) {
      return;
    }
    const normalized = this.clampVolume(volume ?? this.soundVolume);
    this.savingVolume = true;
    try {
      const { fridge } = await Api.setFridgeSettings(
        { soundVolume: normalized } as any,
        this.fridgeService.activeFridge._id,
      );
      this.fridgeService.setActiveFridge({ ...fridge, stateMachine: this.fridgeService.activeFridge?.stateMachine ?? fridge?.stateMachine });
      this.initialSoundVolume =
        (this.fridgeService.activeFridge?.tabletSetting as any)?.soundVolume ?? normalized;
      this.soundVolume = this.initialSoundVolume;
      const msg = await this.resolveTranslation('notification.success.volume_set', {
        volume: this.soundVolume,
      });
      await this.notificationService.notify(msg);
    } finally {
      this.savingVolume = false;
      this.tick();
    }
  }

  async resetTabletSoundVolume() {
    const resetTo = this.initialSoundVolume ?? 1;
    this.soundVolume = resetTo;
    this.tick();
  }

  openVolumeModal() {
    this.tempSoundVolume = this.soundVolume;
    this.volumeModalStatus = true;
    this.tick();
  }

  closeVolumeModal() {
    this.volumeModalStatus = false;
    this.tempSoundVolume = this.soundVolume;
    this.tick();
  }

  async saveVolumeFromModal() {
    await this.saveTabletSoundVolume(this.tempSoundVolume);
    this.volumeModalStatus = false;
  }

  async setMode(params: { factoryModeEnabled?: boolean; stopSaleEnabled?: boolean }) {
    let message = `${params.factoryModeEnabled ? 'Factory' : 'Stop Sale'} Mode`;
    if (params.factoryModeEnabled || params.stopSaleEnabled) {
      const { success } = await ConfirmationModalComponent.show(
        this.modalController,
        `Do you really want to enable ${message}`,
        params.factoryModeEnabled
          ? 'This mode will stop sales and Tablet will provide FULL CONTROL to ANYBODY'
          : 'This mode will stop sales',
      );
      if (!success) {
        return;
      }
      message = `Enable ${message}`;
    } else {
      message = `Disable ${message}`;
    }

    const { fridge } = await Api.setFridgeSettings(params, this.fridgeService.activeFridge._id);

    if (fridge) {
      const msg = await this.resolveTranslation('notification.success.mode', { command: message });
      await this.notificationService.notify(msg);
    }
    // Preserve socket-updated stateMachine — API response may have stale state
    const mergedFridge = {
      ...fridge,
      stateMachine: this.fridgeService.activeFridge?.stateMachine ?? fridge?.stateMachine,
    };
    this.fridgeService.setActiveFridge(mergedFridge);
    this.tick();
  }

  decimalOnlyValidation(event: any) {
    const pattern = /[0-9.,]/;
    const inputChar = String.fromCharCode(event.charCode);

    if (!pattern.test(inputChar)) {
      // invalid character, prevent input
      event.preventDefault();
    }
  }

  numberOnlyValidation(event: any) {
    const pattern = /[0-9]/;
    const inputChar = String.fromCharCode(event.charCode);

    if (!pattern.test(inputChar)) {
      // invalid character, prevent input
      event.preventDefault();
    }
  }

  async executeAiCommand(command) {
    await Api.executeAiCommand(this.fridgeService.activeFridge._id, command);

    const msg = await this.resolveTranslation('notification.success.command', { command });
    await this.notificationService.notify(msg);
  }

  async toggleWeightSensors() {
    this.fridgeService.showWeightSensorsValues = !this.fridgeService.showWeightSensorsValues;
    if (this.fridgeService.showWeightSensorsValues) {
      await this.modalController.dismiss();
      this.fridgeService.fridgeDataChanged.emit();
      this.socketService.getSensors(this.fridgeService.activeFridge._id);
    }
  }

  async toggleFrames() {
    this.fridgeService.streamer.showFrames = !this.fridgeService.streamer.showFrames;
    if (this.fridgeService.streamer.showFrames) {
      this.socketService.getStreamerFrame(this.fridgeService.activeFridge._id);
    } else {
      this.fridgeService.streamer.frames = [];
      this.fridgeService.streamer.packetNumber = 0;
    }
    this.tick();
  }

  async requestNetworkTraffic() {
    this.loadingTraffic = true;
    try {
      const { interfaces } = await Api.getFridgeNetworkTraffic(this.fridgeService.activeFridge._id);
      if (interfaces.length) {
        const maxTotalTx = Math.max(...interfaces.map((i) => i.traffic?.total.tx));
        this.traffic = interfaces.filter((i) => i.traffic?.total.tx === maxTotalTx)[0].traffic;
        if (this.isTraffic(this.traffic)) {
          this.trafficItems = this.traffic.day.map((d) => this.makeDayTrafficLine(d, false));
          this.trafficItems.push(
            ...this.traffic.month.map((m) => this.makeMonthTrafficLine(m, false)),
          );
        }
        if (this.isTrafficV118(this.traffic)) {
          this.trafficItems = this.traffic.days.map((d) => this.makeDayTrafficLine(d, true));
          this.trafficItems.push(
            ...this.traffic.months.map((m) => this.makeMonthTrafficLine(m, true)),
          );
        }
      }
    } catch (err) {}
    this.loadingTraffic = false;
    this.tick();
  }

  async toggleRackViewSize() {
    const current = localStorage.getItem('fridgePageRackSize') ?? this.rackSize;
    const newValue = current === 'small' ? 'big' : 'small';
    localStorage.setItem('fridgePageRackSize', newValue);
    this.rackSize = newValue;
    await this.modalController.dismiss();
    this.fridgeService.fridgeDataChanged.emit();
  }

  async toggleShowAiRecognition() {
    const current = JSON.parse(
      localStorage.getItem('showAiRecognition') || JSON.stringify(this.showAiRecognition),
    );
    const newValue = !current;
    localStorage.setItem('showAiRecognition', JSON.stringify(newValue));
    this.showAiRecognition = newValue;
    await this.modalController.dismiss();
    this.fridgeService.fridgeDataChanged.emit();
  }

  async copyText(val) {
    await navigator.clipboard.writeText(val);
    await this.notificationService.notify(this.i18n.instant('notification.success.ip_copy'));
  }

  async getRackViewSize() {
    return this.rackSize;
  }

  async setPosIp(ip) {
    await Api.setPosIp(this.fridgeService.activeFridge._id, ip);
    this.fridgeService.activeFridge.hardwareInfo.posIp = ip;
    await this.notificationService.notify(this.i18n.instant('notification.success.ip_set'));
  }

  getReservationAmount() {
    const config = this.fridgeService.activeFridge.payment as ReservationConfig;

    return config.reservationAmount || '0';
  }

  getDoorsReservationAmount(): ReservationConfig['doorsConfig'] {
    const config = this.fridgeService.activeFridge.payment as ReservationConfig;
    return config.doorsConfig?.length ? config.doorsConfig : [];
  }

  private trafficString(bytes, baseIsMb: boolean) {
    const divOn = baseIsMb ? 1 : 1024;
    if (bytes > 1024 * 1024 * divOn) {
      return `${(bytes / 1024 / 1024 / divOn).toFixed(3)} Gb`;
    }
    return `${(bytes / 1024 / divOn).toFixed(1)} Mb`;
  }

  private isTraffic(traffic: VnStat.Traffic_v_1_18 | VnStat.Traffic): traffic is VnStat.Traffic {
    return typeof (traffic as VnStat.Traffic).day !== 'undefined';
  }

  private isTrafficV118(
    traffic: VnStat.Traffic_v_1_18 | VnStat.Traffic,
  ): traffic is VnStat.Traffic_v_1_18 {
    return typeof (traffic as VnStat.Traffic_v_1_18).days !== 'undefined';
  }

  private makeDayTrafficLine(
    d: { date: VnStat.DateNumberFields } & VnStat.RxTx,
    baseIsMb,
  ): TrafficItem {
    const month = d.date.month.toString().padStart(2, '0');
    const day = d.date.day.toString().padStart(2, '0');
    return {
      title: `${d.date.year}-${month}-${day}`,
      rx: this.trafficString(d.rx, baseIsMb),
      tx: this.trafficString(d.tx, baseIsMb),
      total: this.trafficString(d.rx + d.tx, baseIsMb),
    };
  }

  private makeMonthTrafficLine(
    d: { date: VnStat.MonthNumberFields } & VnStat.RxTx,
    baseIsMb,
  ): TrafficItem {
    return {
      title: `
        ${d.date.year}-${d.date.month.toString().padStart(2, '0')}
      `,
      rx: this.trafficString(d.rx, baseIsMb),
      tx: this.trafficString(d.tx, baseIsMb),
      total: this.trafficString(d.rx + d.tx, baseIsMb),
    };
  }

  private clampVolume(val: number): number {
    if (Number.isNaN(val)) {
      return 100;
    }
    return Math.max(0, Math.min(100, val));
  }

  private async resolveTranslation(key: string, params?: any): Promise<string> {
    const instant = this.i18n.instant(key, params);
    if (instant && instant !== key) {
      return instant;
    }
    try {
      const value = await this.i18n.get(key, params).toPromise();
      if (typeof value === 'string') {
        return value;
      }
      if (value && typeof value[key] === 'string') {
        return value[key];
      }
    } catch (e) {
      // ignore
    }
    return key;
  }
}
